import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { FileText, X, Edit } from 'lucide-react';
import { VALORES_RASPADINHAS } from './fechoTurnoUtils';

/**
 * Modal de visualização de fecho de turno
 */
const FechoTurnoViewModal = ({
  show,
  fecho,
  onClose,
  onEdit,
  calcularRaspVendidas,
  calcularTotalRasp
}) => {
  if (!show || !fecho) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Visualizar Fecho de Turno
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="space-y-6">
            {/* Data e Turno */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Data</label>
                <div className="px-3 py-2 border rounded-md bg-muted">
                  {new Date(fecho.data + 'T00:00:00').toLocaleDateString('pt-PT')}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Turno</label>
                <div className="px-3 py-2 border rounded-md bg-muted">
                  {fecho.turno}
                </div>
              </div>
            </div>

            {/* Caixa Inicial */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Caixa Inicial</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm mb-2 font-semibold">Caixa Inicial</label>
                    <div className="px-3 py-2 border rounded-md bg-muted font-bold">
                      €{(fecho.caixa_inicial_1 || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm mb-2 font-semibold">Caixa Atual</label>
                    <div className="px-3 py-2 border rounded-md bg-muted font-bold">
                      €{(fecho.caixa_inicial_2 || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm mb-2 font-semibold">Caixa Real</label>
                    <div className="px-3 py-2 border rounded-md bg-warning/10 text-warning font-bold">
                      €{((fecho.caixa_inicial_2 || 0) - (fecho.caixa_inicial_1 || 0)).toFixed(2)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Raspadinhas */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Raspadinhas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-3 text-xs font-semibold mb-2">
                  <div>Valor</div>
                  <div>Stock</div>
                  <div>Recepção</div>
                  <div>S.Inicial</div>
                  <div>S.Final</div>
                  <div>Vendidas</div>
                  <div>Total €</div>
                </div>
                <div className="space-y-3">
                  {VALORES_RASPADINHAS.map(valor => {
                    const stock = fecho[`stock_${valor}`] || 0;
                    const recepcao = fecho[`recepcao_${valor}`] || 0;
                    const sFinal = fecho[`s_final_${valor}`] || 0;
                    const sInicial = stock + recepcao;
                    const vendidas = calcularRaspVendidas(fecho, valor);
                    const total = calcularTotalRasp(fecho, valor);
                    
                    return (
                      <div key={valor} className="grid grid-cols-7 gap-3 items-center border-b border-border pb-2">
                        <div className="font-semibold">{valor}€</div>
                        <div className="px-2 py-1 bg-muted rounded text-center">{stock}</div>
                        <div className="px-2 py-1 bg-muted rounded text-center">{recepcao}</div>
                        <div className="px-2 py-1 bg-muted rounded text-center">{sInicial}</div>
                        <div className="px-2 py-1 bg-muted rounded text-center">{sFinal}</div>
                        <div className="px-2 py-1 bg-success/10 text-success rounded text-center font-semibold">{vendidas}</div>
                        <div className="px-2 py-1 bg-accent/10 text-accent rounded text-center font-semibold">€{total.toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Totais das Raspadinhas */}
                <div className="mt-4 pt-4 border-t-2 border-border">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gradient-card rounded-lg border border-success/20">
                      <label className="block text-sm font-semibold mb-2 text-success">Total Vendidas (valor €)</label>
                      <div className="text-2xl font-bold text-success">
                        €{VALORES_RASPADINHAS.reduce((total, valor) => 
                          total + calcularTotalRasp(fecho, valor), 0
                        ).toFixed(2)}
                      </div>
                    </div>
                    
                    <div className="p-3 bg-gradient-card rounded-lg border border-primary/20">
                      <label className="block text-sm font-semibold mb-2 text-primary">Total Stock Final (valor €)</label>
                      <div className="text-2xl font-bold text-primary">
                        €{VALORES_RASPADINHAS.reduce((total, valor) => 
                          total + ((fecho[`s_final_${valor}`] || 0) * valor), 0
                        ).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Prémios e Vendas */}
            <Card className="bg-gradient-card">
              <CardHeader>
                <CardTitle className="text-base">Prémios e Vendas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-2">Vendas</label>
                    <div className="px-3 py-2 border rounded-md bg-muted">
                      €{(fecho.premios_master_1_15 || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Prémios</label>
                    <div className="px-3 py-2 border rounded-md bg-muted">
                      €{(fecho.premios_total || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Totais e Diferenças */}
            <Card className="bg-gradient-card">
              <CardHeader>
                <CardTitle className="text-base">Totais e Diferenças</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm mb-2 font-semibold">Total expectável</label>
                    <div className="px-3 py-2 border rounded-md bg-warning/10 text-warning font-bold">
                      €{(fecho.total_expectavel || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm mb-2 font-semibold text-primary">Caixa Final</label>
                    <div className="px-3 py-2 border rounded-md bg-primary/10 text-primary font-bold">
                      €{(fecho.caixa_final || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm mb-2 font-semibold text-destructive">Diferença</label>
                    <div className={`px-3 py-2 border rounded-md font-bold ${
                      Math.abs(fecho.diferenca || 0) < 0.01 
                        ? 'bg-success/10 text-success' 
                        : 'bg-destructive/10 text-destructive'
                    }`}>
                      €{(fecho.diferenca || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Observações */}
            {fecho.observacoes && (
              <div>
                <label className="block text-sm font-medium mb-2">Observações</label>
                <div className="px-3 py-2 border rounded-md bg-muted">
                  {fecho.observacoes}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Fechar
              </Button>
              <Button 
                type="button" 
                onClick={() => {
                  onClose();
                  onEdit(fecho);
                }}
                className="bg-primary hover:bg-primary/90"
              >
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FechoTurnoViewModal;
